/**
 * Cliente: normalización del texto de búsqueda de clientes.
 *
 * Espejo de las reglas del sanitizador que ApiCore aplica del lado del servidor
 * (diseño "Customer search redesign", sección Sanitizer). Sirve para dos cosas:
 * mostrar la fila de ayuda en el desplegable y evitar llamadas que el servidor
 * ya sabe que no van a devolver nada. La autoridad es ApiCore; este archivo y el
 * fixture `__tests__/search-sanitizer-cases.json` se copian verbatim a los demás
 * frontends para que las implementaciones no se separen.
 */

export const SEARCH_MAX_LENGTH = 80;
export const MIN_IDENTIFICATION_DIGITS = 4;
export const MAX_TOKENS = 5;
export const MAX_TOKEN_LENGTH = 30;

/** Mínimo por defecto. El valor real llega del API en `minTokenLength`. */
export const DEFAULT_MIN_TOKEN_LENGTH = 3;

export const SEARCH_STOPWORDS = [
  "de",
  "del",
  "la",
  "las",
  "el",
  "los",
  "y",
  "e",
  "con",
  "por",
  "para",
  "una",
  "uno",
  "sin",
];

export const SEARCH_STRINGS = {
  placeholder: "Nombre o cédula/RIF",
  belowMinimum:
    "Sigue escribiendo: 3 letras del nombre o 4 dígitos de la cédula/RIF",
  emailHint: "Para correo usa la búsqueda avanzada",
  phoneHint: "Para teléfono usa la búsqueda avanzada",
  empty: 'No se encontró ningún cliente con "{q}"',
  error: "No se pudo buscar · Reintentar",
  retry: "Reintentar",
};

/**
 * Los nombres son los que emite el API (SearchRoute / BelowMinimumReason en ApiCore) y
 * los que trae el fixture compartido. No se traducen aquí: el fixture es el contrato.
 */
export type SearchRoute =
  | "IdentificationPrefix"
  | "NameFullText"
  | "BelowMinimum";
export type SearchHint = "None" | "Email" | "Phone";
export type SearchReason =
  | "None"
  | "Empty"
  | "NoTokenGe3"
  | "IdentificationLt4Digits"
  | "EmailHint"
  | "OnlyStopwords";

export interface NormalizedSearch {
  /** Entrada recortada a 80 caracteres y normalizada a NFC. */
  raw: string;
  /** Letra del tipo de identificación (V, E, J, G, P) si el usuario la escribió. */
  type: string | null;
  /** Dígitos de la identificación, sin puntos ni guiones. */
  identification: string | null;
  /** Tokens que irían a full text, en minúsculas. */
  tokens: string[];
  route: SearchRoute;
  hint: SearchHint;
  reason: SearchReason;
  /**
   * Identidad de la búsqueda. Cadena vacía = no hay nada que consultar.
   * El hook usa esta clave para no repetir una consulta ya lanzada.
   */
  key: string;
}

export interface NormalizeSearchOptions {
  minTokenLength?: number;
}

// Identificación: letra opcional, luego sólo dígitos con puntos, espacios o
// guiones. `[0-9]` a propósito, no `\d` (que en otros motores abarca dígitos
// Unicode y no es lo que guarda IdentificationNumber).
const IDENTIFICATION_RE = /^\s*([VEJGP])?\s*[-.]?\s*([0-9][0-9.\s-]*)\s*$/i;
const PASSPORT_RE = /^\s*P\s*[-.]?\s*([A-Z0-9]{4,20})\s*$/i;
const ASCII_DIGITS_RE = /^[0-9]+$/;
const PHONE_HINT_RE = /^0[0-9]{9,10}$/;

// tsconfig apunta a ES5, donde el flag /u en un literal no compila: hay que
// construir la expresión con `new RegExp`.
const NON_WORD_RE = new RegExp("[^\\p{L}\\p{Nd}]+", "gu");

export const digitsOnly = (input: string): string =>
  (input ?? "").replace(/[^0-9]/g, "");

/**
 * Teléfono tal como lo espera el API: sin `+ ( ) -` ni espacios y sin el 0
 * inicial ni el código de país, porque PhoneNumbers guarda sólo el número.
 */
export const normalizePhoneInput = (input: string): string => {
  const digits = digitsOnly((input ?? "").replace(/[+()\-\s]/g, ""));

  if (digits.indexOf("58") === 0 && digits.length > 10) {
    return digits.slice(2);
  }
  if (digits.charAt(0) === "0") {
    return digits.slice(1);
  }
  return digits;
};

export const normalizeIdentificationInput = (
  input: string,
  isPassport: boolean = false
): string => {
  if (!isPassport) {
    return digitsOnly(input);
  }
  return (input ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
};

export const normalizeEmailInput = (input: string): string =>
  (input ?? "").trim().toLowerCase();

interface IdentificationMatch {
  type: string | null;
  identification: string | null;
  passportToken: string | null;
}

/** Se evalúa sobre la entrada cruda, antes de limpiar ningún carácter. */
export const detectIdentification = (raw: string): IdentificationMatch | null => {
  const match = IDENTIFICATION_RE.exec(raw);
  if (match) {
    return {
      type: match[1] ? match[1].toUpperCase() : null,
      identification: digitsOnly(match[2]),
      passportToken: null,
    };
  }

  const passport = PASSPORT_RE.exec(raw);
  if (passport) {
    return {
      type: "P",
      identification: null,
      passportToken: passport[1].toLowerCase(),
    };
  }

  return null;
};

/** También sobre la entrada cruda: un correo o un teléfono van al modal. */
export const detectHint = (raw: string): SearchHint => {
  if (raw.indexOf("@") >= 0) {
    return "Email";
  }
  if (PHONE_HINT_RE.test(raw.replace(/[+()\-\s]/g, ""))) {
    return "Phone";
  }
  return "None";
};

interface TokenizeResult {
  tokens: string[];
  /** Palabras de dos o más caracteres, antes de aplicar ancla y palabras vacías. */
  words: string[];
}

const tokenizeInternal = (
  raw: string,
  minTokenLength: number = DEFAULT_MIN_TOKEN_LENGTH
): TokenizeResult => {
  const cleaned = raw.replace(NON_WORD_RE, " ").trim();
  if (cleaned === "") {
    return { tokens: [], words: [] };
  }

  let tokens = cleaned
    .split(" ")
    .filter((token) => token !== "")
    .map((token) => token.toLowerCase());

  // Tokens de un carácter nunca sirven.
  tokens = tokens.filter((token) => token.length >= 2);
  const words = tokens.slice();

  // Los de dos sólo sobreviven acompañados de un ancla de 3 o más, y sólo
  // cuando el API dice que acepta mínimo 2 ("EY Consultores").
  const hasAnchor = tokens.some((token) => token.length >= 3);
  tokens = tokens.filter(
    (token) => token.length >= 3 || (minTokenLength === 2 && hasAnchor)
  );

  tokens = tokens.filter((token) => SEARCH_STOPWORDS.indexOf(token) === -1);
  tokens = tokens.map((token) => token.slice(0, MAX_TOKEN_LENGTH));
  tokens = tokens.slice(0, MAX_TOKENS);

  // Un token de puros dígitos dentro de un nombre ("Inversiones 2000") sólo
  // vale como prefijo si tiene al menos 4.
  tokens = tokens.filter(
    (token) =>
      !ASCII_DIGITS_RE.test(token) || token.length >= MIN_IDENTIFICATION_DIGITS
  );

  return { tokens, words };
};

export const tokenize = (
  raw: string,
  minTokenLength: number = DEFAULT_MIN_TOKEN_LENGTH
): string[] => tokenizeInternal(raw, minTokenLength).tokens;

const belowMinimum = (
  raw: string,
  reason: SearchReason,
  hint: SearchHint,
  type: string | null = null,
  identification: string | null = null
): NormalizedSearch => ({
  raw,
  type,
  identification,
  tokens: [],
  route: "BelowMinimum",
  hint,
  reason,
  key: "",
});

export const normalizeSearch = (
  input: string,
  options: NormalizeSearchOptions = {}
): NormalizedSearch => {
  const minTokenLength = options.minTokenLength ?? DEFAULT_MIN_TOKEN_LENGTH;
  const raw = (input ?? "").trim().normalize("NFC").slice(0, SEARCH_MAX_LENGTH);

  if (raw === "") {
    return belowMinimum(raw, "Empty", "None");
  }

  const hint = detectHint(raw);

  // Un nombre nunca coincide con un correo: no hay consulta que hacer.
  if (hint === "Email") {
    return belowMinimum(raw, "EmailHint", "Email");
  }

  const identification = detectIdentification(raw);

  if (identification && identification.identification !== null) {
    const digits = identification.identification;

    if (digits.length < MIN_IDENTIFICATION_DIGITS) {
      return belowMinimum(
        raw,
        "IdentificationLt4Digits",
        hint,
        identification.type,
        digits
      );
    }

    return {
      raw,
      type: identification.type,
      identification: digits,
      tokens: [],
      route: "IdentificationPrefix",
      hint,
      reason: "None",
      key: `id:${digits}`,
    };
  }

  // Pasaporte: alfanumérico, va a full text sobre las dos columnas.
  if (identification && identification.passportToken) {
    return {
      raw,
      type: "P",
      identification: null,
      tokens: [identification.passportToken],
      route: "NameFullText",
      hint,
      reason: "None",
      key: `ft:${identification.passportToken}`,
    };
  }

  const { tokens, words } = tokenizeInternal(raw, minTokenLength);

  if (!tokens.some((token) => token.length >= 3)) {
    // "de la" no es un texto corto: es un texto que el full text descarta entero.
    const onlyStopwords =
      words.length > 0 &&
      words.every((word) => SEARCH_STOPWORDS.indexOf(word) !== -1);

    return belowMinimum(raw, onlyStopwords ? "OnlyStopwords" : "NoTokenGe3", hint);
  }

  return {
    raw,
    type: null,
    identification: null,
    tokens,
    route: "NameFullText",
    hint,
    reason: "None",
    key: `ft:${tokens.join(" ")}`,
  };
};

/** Texto que recibe el endpoint legacy `GET /Account?idStr=`. */
export const legacyQueryText = (normalized: NormalizedSearch): string => {
  if (normalized.route === "IdentificationPrefix") {
    return normalized.identification ?? "";
  }
  if (normalized.route === "NameFullText") {
    return normalized.tokens.join(" ");
  }
  return "";
};

export const hintMessage = (hint: SearchHint): string => {
  if (hint === "Email") {
    return SEARCH_STRINGS.emailHint;
  }
  if (hint === "Phone") {
    return SEARCH_STRINGS.phoneHint;
  }
  return "";
};

export const emptyMessage = (query: string): string =>
  SEARCH_STRINGS.empty.replace("{q}", query);

/**
 * Shared text helpers for the consignee book search (Favoritos / Recientes).
 *
 * `normalizeSearchText` is applied to BOTH the query and every field value, so
 * the client-side filter is accent- and case-insensitive. NFD decomposition
 * maps "ñ" to "n" + U+0303 and stripping the combining marks leaves a plain
 * "n", so "peña" also matches "pena". That is accepted.
 *
 * `sanitizeFulltextQuery` is a port of the Django implementation (Leg 4): it
 * protects the SQL Server FULLTEXT CONTAINS predicate from characters that
 * would make it throw. Reserved characters are REPLACED by a space, never
 * deleted, so "O'Brien" becomes "O Brien" (what the word breaker does) instead
 * of the token-merging "OBrien".
 */

const COMBINING_MARKS = /[̀-ͯ]/g;
// The hyphen is not reserved by FULLTEXT, but the stored procedure drops any
// token containing one (`CHARINDEX('-', value) = 0`, verified 2026-09-12), so
// "V-12345678" reached it as a token it threw away, leaving an empty token set
// and the unfiltered book. Split on it instead and the digits become a real
// token that CONTAINS can match against the identification columns.
const FULLTEXT_RESERVED = /["'()[\]{}~*-]/g;
const SEARCH_PUNCTUATION = /[-.\s]/g;

export const normalizeSearchText = (value: string): string =>
  value.normalize("NFD").replace(COMBINING_MARKS, "").toLowerCase();

export const sanitizeFulltextQuery = (search: string): string =>
  search.replace(FULLTEXT_RESERVED, " ").replace(/\s+/g, " ").trim();

/**
 * Identification numbers and phones are matched punctuation-insensitively on
 * both sides, so the "V-12.345.678" the teller reads on screen matches whether
 * they type "v-12345678", "V12345678" or "12.345.678".
 */
export const stripSearchPunctuation = (value: string): string =>
  value.replace(SEARCH_PUNCTUATION, "");

/**
 * The exact string compared against `serverQuery` to decide whether a whole-book
 * server search still covers the text in the box. Sanitising first means
 * trailing or doubled spaces never break the comparison.
 */
export const toServerQueryForm = (search: string): string =>
  normalizeSearchText(sanitizeFulltextQuery(search));

/**
 * The tokens the stored procedure will actually search on. It drops anything
 * one character long (`LEN(RTRIM(LTRIM(value))) > 1`), and when nothing
 * survives it abandons the search entirely and returns the plain capped book —
 * so a query with no surviving token is an expensive call that answers with
 * what we already have cached.
 */
export const serverSearchTokens = (serverQueryForm: string): string[] =>
  serverQueryForm.split(" ").filter((token) => token.length > 1);

/**
 * Is a whole-book result fetched for `previous` still sound for `next`?
 *
 * The procedure joins its tokens with OR, not AND
 * (`STRING_AGG('"' + value + '*"', ' OR ')`, verified 2026-09-12), so ADDING a
 * token WIDENS the server's answer: a result fetched for "mar" is missing
 * everything that matches only "lopez", and reusing it would let the dropdown
 * claim "Sin coincidencias en su libreta" about someone who is in it.
 * Extending a token narrows the answer instead — `"maria*"` is a subset of
 * `"mar*"` — so that alone stays covered.
 */
export const serverQueryStillCovers = (
  previous: string,
  next: string
): boolean => {
  const before = serverSearchTokens(previous);
  const after = serverSearchTokens(next);

  if (before.length === 0 || before.length !== after.length) return false;

  return before.every((token, index) => after[index].startsWith(token));
};
